import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LANG_INFO, langOfPath, preferredLang, type Lang } from "../guides/i18n";

/* Offers the translated Deadlock page to a visitor whose browser prefers one
   of its languages (or, for the Russian-speaking countries, whose time zone
   says so). An offer, not a redirect: a redirect by language would also send
   crawlers away from the English pages and get them indexed as duplicates.

   Shown on the English pages only, dismissed for good with the ×. Reads the
   browser after mount, so the prerendered HTML is the same for everyone. */

const KEY = "veyra.langOffer.dismissed";

export default function LanguageSuggest() {
  const { pathname } = useLocation();
  const [lang, setLang] = useState<Lang | null>(null);

  useEffect(() => {
    try {
      if (localStorage.getItem(KEY)) return;
    } catch {
      /* storage blocked: still offer */
    }
    setLang(preferredLang());
  }, []);

  /* Not on a page that is already translated, and not inside the purchase,
     account or admin screens, where it would only be in the way. */
  if (!lang || langOfPath(pathname) || /^\/(admin|pricing|downloads|profile|invoice|payment|login|signup)/.test(pathname)) {
    return null;
  }
  const info = LANG_INFO[lang];
  const dismiss = () => {
    try {
      localStorage.setItem(KEY, "1");
    } catch {
      /* fine: hidden for this visit */
    }
    setLang(null);
  };

  return (
    <div
      lang={info.hreflang}
      className="fixed inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-white/10 bg-ground-2/95 px-4 py-3 text-sm text-white shadow-2xl backdrop-blur sm:bottom-5"
    >
      <span className="flex-1">{info.offer}</span>
      <Link
        to={`/${lang}`}
        onClick={dismiss}
        className="inline-flex min-h-9 items-center rounded-full bg-primary px-4 font-semibold text-[#06211f] transition-colors hover:bg-primary-light"
      >
        {info.native} · {info.open}
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="grid h-8 w-8 place-items-center rounded-full text-white/50 transition-colors hover:bg-white/10 hover:text-white"
      >
        ×
      </button>
    </div>
  );
}
